import React from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography, useTheme as useMuiTheme } from "@mui/material";
import { useTheme } from "../contexts/ThemeContext";
import { APP_NAME } from "../helpers/constants";

export interface AskPrism7ButtonProps {
  /** Whether embeddings are completed (data sources connected) */
  hasEmbeddings: boolean;
  /** Whether the button should be disabled (e.g. thread/channel archived or no embeddings) */
  disabled: boolean;
  /** True while subscription validation runs after click */
  loading?: boolean;
  /** Called when the button is clicked to open the Ask panel (may be async) */
  onOpen: () => void | Promise<void>;
}

/**
 * Ask Prism7 trigger button: logo + "ASK {APP_NAME}" in uppercase, styled like TASK/APPROVAL.
 * Opens the Ask Prism7 right panel when clicked.
 */
function AskPrism7Button({ hasEmbeddings, disabled, loading = false, onOpen }: AskPrism7ButtonProps) {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const appName = APP_NAME || "Prism 7";
  // Treat loading as disabled for tooltip copy
  const isBlocked = disabled || loading;

  const tooltipTitle = hasEmbeddings && !isBlocked
    ? `Ask ${appName}`
    : loading
      ? `Checking subscription…`
      : `Please add and connect data sources to use Ask ${appName} functionality.`;

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <span style={{ flexShrink: 0 }}>
        <IconButton
          data-testid="thread-ask-shay-button"
          size="small"
          disabled={disabled}
          onClick={() => {
            void onOpen();
          }}
          sx={{
            flexShrink: 0,
            color: disabled ? muiTheme.palette.text.disabled : muiTheme.palette.text.secondary,
            "&:hover": {
              backgroundColor: disabled ? "transparent" : muiTheme.palette.action.hover,
              color: disabled ? muiTheme.palette.text.disabled : muiTheme.palette.text.secondary,
            },
            "&.Mui-disabled": {
              color: muiTheme.palette.text.disabled,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : "#f5f5f5",
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              minWidth: "max-content",
              flexShrink: 0,
              border: `1px solid ${isDarkMode ? muiTheme.palette.grey[700] : "#e0e0e0"}`,
              cursor: loading ? "default" : "pointer",
            }}
          >
            {/* Spinner during subscription validation; logo otherwise */}
            {loading ? (
              <CircularProgress size={14} thickness={5} sx={{ flexShrink: 0 }} />
            ) : (
              <Box
                component="img"
                src={disabled ? "/logo-black.png" : "/logo.png"}
                alt={`Ask ${appName}`}
                sx={{ width: 16, height: 16, display: "block", flexShrink: 0 }}
              />
            )}
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1,
                color: disabled
                  ? muiTheme.palette.text.disabled
                  : isDarkMode
                    ? muiTheme.palette.text.secondary
                    : "#666",
                whiteSpace: "nowrap",
                flexShrink: 0,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Ask {appName}
            </Typography>
          </Box>
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default AskPrism7Button;
